'use client'

import { type ErrorInfo } from 'react'

import { useTranslations } from 'next-intl'

import { env } from '@/lib/env'

interface ErrorBoundaryFallbackProps {
  error: Error
  errorInfo: ErrorInfo | null
  onReload: () => void
}

export function ErrorBoundaryFallback({
  error,
  errorInfo,
  onReload
}: ErrorBoundaryFallbackProps) {
  const t = useTranslations('common.errors')

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-lg border border-red-200 bg-red-50 p-6">
        <h2 className="mb-4 text-xl font-semibold text-red-800">
          {t('genericTitle')}
        </h2>
        {env.NEXT_PUBLIC_DEV_MODE && error ? (
          <div className="space-y-4">
            <div>
              <p className="font-medium text-red-900">{t('errorMessage')}</p>
              <pre className="mt-1 overflow-auto rounded bg-red-100 p-2 text-sm text-red-800">
                {error.message}
              </pre>
            </div>
            {error.stack && (
              <div>
                <p className="font-medium text-red-900">{t('stackTrace')}</p>
                <pre className="mt-1 max-h-64 overflow-auto rounded bg-red-100 p-2 text-xs text-red-800">
                  {error.stack}
                </pre>
              </div>
            )}
            {errorInfo?.componentStack && (
              <div>
                <p className="font-medium text-red-900">
                  {t('componentStack')}
                </p>
                <pre className="mt-1 max-h-64 overflow-auto rounded bg-red-100 p-2 text-xs text-red-800">
                  {errorInfo.componentStack}
                </pre>
              </div>
            )}
          </div>
        ) : (
          <p className="text-red-700">{t('genericDescription')}</p>
        )}
        <button
          className="mt-4 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
          onClick={onReload}
          type="button"
        >
          {t('reloadPage')}
        </button>
      </div>
    </div>
  )
}
